import { IErrorReport, IErrorReportInterceptor } from './interface'
import { loadScript } from '../utils/load-script'

/**
 * 错误上报初始化选项
 * @interface IErrorReportInitializerOptions
 * @property {string} dsn - Sentry DSN
 * @property {string} environment - 环境（prod / test）
 * @property {string} release - 发布版本
 * @property {number} replaysSessionSampleRate - 会话重放采样率
 * @property {number} replaysOnErrorSampleRate - 错误重放采样率
 */
export interface IErrorReportInitializerOptions {
  dsn: string
  environment: string
  release: string
  replaysSessionSampleRate: number
  replaysOnErrorSampleRate: number
}

/**
 * Sentry 错误上报
 */
export class SentryErrorReport implements IErrorReport {
  /**
   * 初始化的 Promise，直到初始化完成才会 resolve
   * @property {Promise<void>} initializePromise
   * @private
   */
  private initializePromise!: Promise<void>

  /**
   * 初始化完成时调用的函数
   * @property {() => void} initializePromiseResolver
   * @private
   */
  private initializePromiseResolver!: () => void

  /**
   * 构造函数，初始化 initializePromise 和 initializePromiseResolver
   * @constructor
   */
  constructor() {
    /**
     * 初始化的 Promise，直到初始化完成才会 resolve
     * @type {Promise<void>}
     */
    this.initializePromise = new Promise((resolve) => {
      /**
       * 初始化完成时调用的函数
       * @type {() => void}
       */
      this.initializePromiseResolver = resolve
    })
  }

  /**
   * 初始化
   * @param interceptor 拦截器
   */
  async initialize(interceptor?: IErrorReportInterceptor<IErrorReportInitializerOptions>): Promise<void> {
    let options: Partial<IErrorReportInitializerOptions> = {
      environment: window.location.hostname.indexOf('test-zt') > -1 ? 'test' : 'prod',
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1,
    }

    if (interceptor) {
      Object.assign(options, interceptor())
    } else {
      Object.assign(options, this.readExternalInitializeOptions())
    }

    if (!options.dsn) {
      throw new Error('dsn is required')
    }

    if (!options.release) {
      throw new Error('release is required')
    }

    if (options.dsn.indexOf('error-report.xoyo.com') > -1) {
      await this.loadLegacySentry(options as IErrorReportInitializerOptions)
      this.initializePromiseResolver()
      return
    }

    if (options.dsn.indexOf('ssg-sentry.amazingseasun.com') > -1) {
      await this.loadSentry(options as IErrorReportInitializerOptions)
      this.initializePromiseResolver()
      return
    }

    throw new Error('dsn is not supported due to invalid domain = ' + options.dsn)
  }

  /**
   * 加载 sentry
   * @param options 参数
   */
  private async loadSentry(options: IErrorReportInitializerOptions): Promise<void> {
    const scriptEl = document.createElement('script')
    scriptEl.innerHTML = `var __sentryConfig=${JSON.stringify(options)};!function(n,e,r,t,i,a,c,s){var u=s;var p=[];function l(n){return"e"in n}function d(n){return"p"in n}function _(n){return"f"in n}var v=[];function y(n){u&&(l(n)||d(n)||_(n)&&n.f.indexOf("capture")>-1||_(n)&&n.f.indexOf("showReportDialog")>-1)&&m(),v.push(n)}function g(){y({e:[].slice.call(arguments)})}function h(n){y({p:n})}function E(){try{n.SENTRY_SDK_SOURCE="loader";var e=n[i],o=e.init;e.init=function(i){n.removeEventListener(r,g),n.removeEventListener(t,h);var a=c;for(var s in i)Object.prototype.hasOwnProperty.call(i,s)&&(a[s]=i[s]);!function(n,e){var r=n.integrations||[];if(!Array.isArray(r))return;var t=r.map((function(n){return n.name}));n.tracesSampleRate&&-1===t.indexOf("BrowserTracing")&&(e.BrowserTracing?r.push(new e.BrowserTracing):e.browserTracingIntegration&&r.push(e.browserTracingIntegration()));(n.replaysSessionSampleRate||n.replaysOnErrorSampleRate)&&-1===t.indexOf("Replay")&&(e.Replay?r.push(new e.Replay):e.replayIntegration&&r.push(e.replayIntegration()));n.integrations=r}(a,e),o(a)},setTimeout((function(){return function(e){try{"function"==typeof n.sentryOnLoad&&(n.sentryOnLoad(),n.sentryOnLoad=void 0);for(var r=0;r<p.length;r++)"function"==typeof p[r]&&p[r]();p.splice(0);for(r=0;r<v.length;r++){_(o=v[r])&&"init"===o.f&&e.init.apply(e,o.a)}L()||e.init();var t=n.onerror,i=n.onunhandledrejection;for(r=0;r<v.length;r++){var o;if(_(o=v[r])){if("init"===o.f)continue;e[o.f].apply(e,o.a)}else l(o)&&t?t.apply(n,o.e):d(o)&&i&&i.apply(n,[o.p])}}catch(n){console.error(n)}}(e)}))}catch(n){console.error(n)}}var O=!1;function m(){if(!O){O=!0;var n=e.scripts[0],r=e.createElement("script");r.src=a,r.crossOrigin="anonymous",r.addEventListener("load",E,{once:!0,passive:!0}),n.parentNode.insertBefore(r,n)}}function L(){var e=n.__SENTRY__;return!(void 0===e||!e.hub||!e.hub.getClient())}n[i]=n[i]||{},n[i].onLoad=function(n){L()?n():p.push(n)},n[i].forceLoad=function(){setTimeout((function(){m()}))},["init","addBreadcrumb","captureMessage","captureException","captureEvent","configureScope","withScope","showReportDialog"].forEach((function(e){n[i][e]=function(){y({f:e,a:arguments})}})),n.addEventListener(r,g),n.addEventListener(t,h),u||setTimeout((function(){m()}))}(window,document,"error","unhandledrejection","Sentry",'https://static-web.amazingseasuncdn.com/xassets/lib/bundle/7.104.0/bundle.tracing.min.js',__sentryConfig,false);`
    document.head.appendChild(scriptEl)
  }

  /**
   * 加载过时的 sentry
   * @param options 参数
   */
  private async loadLegacySentry(options: IErrorReportInitializerOptions): Promise<void> {
    const src = '//zhcdn01.xoyo.com/xassets/lib/raven/3.27.0/raven.min.js'
    await loadScript(src).then(() => {
      ;(window as any).Raven.config(options.dsn, options).install()
    })
  }

  /**
   * 上报错误
   * @param error 错误
   */
  report(error: Error): void {
    this.initializePromise.then(() => {
      ;(window as any).Sentry?.captureException(error)
      ;(window as any).Raven?.captureException(error)
    })
  }

  /**
   * 读取外部初始化配置
   * @private
   */
  private readExternalInitializeOptions() {
    const content = document.querySelector('meta[name="sentry-options"]')?.getAttribute('content')
    if (content) {
      try {
        return JSON.parse(content)
      } catch (error: unknown) {
        throw new Error(
          `sentry-options content is not valid JSON, error = ${(error as Error).message}, stack = ${(error as Error).stack}`,
        )
      }
    }
    return null
  }
}
